"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSession, signOut } from "next-auth/react";

type Employee = {
  id: string;
  name: string;
  userId?: string;
  hasLogin?: boolean;
  profileImagePath?: string | null;
};

export default function NavBar() {
  const { data: session } = useSession();
  const displayName = session?.user?.name ?? (session?.user as any)?.username;
  const currentUserId = (session?.user as any)?.id as string | undefined;
  const [hideNav, setHideNav] = useState(false);
  const [canRestoreNav, setCanRestoreNav] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const [selfEmployee, setSelfEmployee] = useState<Employee | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const check = () => {
      const modalHidden = new URLSearchParams(window.location.search).get("modal") === "1";
      const navHidden = document.body?.dataset?.navHidden === "1";
      setHideNav(modalHidden || navHidden);
      setCanRestoreNav(navHidden && !modalHidden);
    };
    check();
    window.addEventListener("popstate", check);
    window.addEventListener("nav-visibility-change", check);
    return () => {
      window.removeEventListener("popstate", check);
      window.removeEventListener("nav-visibility-change", check);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hoverNone = window.matchMedia("(hover: none)");
    const hoverAny = window.matchMedia("(any-hover: hover)");
    const coarse = window.matchMedia("(pointer: coarse)");
    const fine = window.matchMedia("(pointer: fine)");
    const fineAny = window.matchMedia("(any-pointer: fine)");
    const smallScreen = window.matchMedia("(max-width: 900px)");
    const mobileUserAgent = /Android|iPhone|iPad|iPod|Mobile|SamsungBrowser/i.test(window.navigator.userAgent || "");
    const update = () => {
      const touchPoints = navigator.maxTouchPoints || 0;
      const touchLike = touchPoints > 0 && (hoverNone.matches || coarse.matches);
      setIsTouch(mobileUserAgent || (touchPoints > 0 && smallScreen.matches) || (touchLike && !hoverAny.matches && !fine.matches && !fineAny.matches));
    };
    update();
    const mqs = [hoverNone, hoverAny, coarse, fine, fineAny, smallScreen];
    mqs.forEach((mq) => mq.addEventListener ? mq.addEventListener("change", update) : mq.addListener(update));
    return () => {
      mqs.forEach((mq) => mq.removeEventListener ? mq.removeEventListener("change", update) : mq.removeListener(update));
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let applied = false;
    const applyDesktop = () => {
      if (applied) return;
      applied = true;
      if (document.body.dataset.uiMode !== "desktop") {
        document.body.dataset.uiMode = "desktop";
        window.dispatchEvent(new Event("ui-mode-change"));
      }
    };
    const supportsPointer = "PointerEvent" in window;
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType === "mouse") applyDesktop();
    };
    const onMouseMove = (e: MouseEvent) => {
      if ((e as any).sourceCapabilities?.firesTouchEvents) return;
      applyDesktop();
    };
    if (supportsPointer) {
      window.addEventListener("pointermove", onPointerMove, { passive: true });
    } else {
      window.addEventListener("mousemove", onMouseMove, { passive: true });
    }
    return () => {
      if (supportsPointer) window.removeEventListener("pointermove", onPointerMove);
      else window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.dataset.uiMode = isTouch ? "touch" : "desktop";
    window.dispatchEvent(new Event("ui-mode-change"));
  }, [isTouch]);

  useEffect(() => {
    if (!currentUserId) {
      setSelfEmployee(null);
      return;
    }
    let active = true;
    const loadSelf = async () => {
      try {
        const res = await fetch("/api/employees");
        const data = await res.json().catch(() => []);
        if (!active || !Array.isArray(data)) return;
        const match = (data as Employee[]).find((emp) => emp.userId === currentUserId) ?? null;
        setSelfEmployee(match);
      } catch {
        if (active) setSelfEmployee(null);
      }
    };
    void loadSelf();
    return () => {
      active = false;
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 900px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);

  const avatarLabel = useMemo(() => {
    const base = (displayName ?? selfEmployee?.name ?? "?").trim();
    return base.charAt(0).toUpperCase() || "?";
  }, [displayName, selfEmployee]);
  const isMobile = isTouch && isNarrow;
  const baseLink = "inline-flex min-h-10 items-center justify-center border rounded px-2 py-1 text-center text-sm font-medium transition-colors";
  const neutralLink = `${baseLink} bg-white text-gray-800 border-gray-300 hover:bg-gray-50 hover:border-gray-400`;
  const accentLink = `${baseLink} bg-emerald-600/15 text-emerald-700 border-emerald-300 hover:bg-emerald-600/25`;
  const darkLink = `${baseLink} bg-black text-white border-black hover:bg-gray-900`;
  const navClass = isMobile
    ? "max-w-6xl mx-auto flex flex-col gap-2 px-3 py-2 text-sm relative"
    : "max-w-6xl mx-auto flex items-center gap-4 px-3 py-2 text-base relative flex-nowrap";
  const linkRowClass = isMobile
    ? "grid grid-cols-2 gap-2 pb-1 sm:grid-cols-3"
    : "flex items-center gap-4 flex-1 flex-nowrap whitespace-nowrap overflow-x-auto min-w-0";
  const authRowClass = isMobile
    ? "flex items-center gap-3 justify-end w-full pt-1"
    : "flex items-center gap-3 ml-auto whitespace-nowrap";

  if (hideNav) {
    if (!isMobile || !canRestoreNav) return null;
    return (
      <button
        type="button"
        className="fixed left-1/2 top-0 z-50 -translate-x-1/2 border border-t-0 border-gray-300 bg-white/95 text-sm shadow-sm"
        title="Menü einblenden"
        onClick={() => {
          delete document.body.dataset.navHidden;
          window.dispatchEvent(new Event("nav-visibility-change"));
        }}
        style={{ width: 72, height: 30, borderBottomLeftRadius: 18, borderBottomRightRadius: 18 }}
      >
        Menü
      </button>
    );
  }

  return (
    <header className="border-b border-red-200/60 bg-white/95 backdrop-blur sticky top-0 z-[9000]">
      <nav className={navClass}>
        <div className={linkRowClass} style={isMobile ? { WebkitOverflowScrolling: "touch" } : undefined}>
          <Link href="/" className={darkLink}>Home</Link>
          <Link href="/schedule" className={neutralLink}>Arbeitsplan</Link>
          <Link href="/employees" className={neutralLink}>Mitarbeiterdaten</Link>
          <Link href="/orders" className={neutralLink}>Aufträge</Link>
          <Link href="/expenses" className={neutralLink}>Spesen</Link>
          <Link href="/timesheet" className={neutralLink}>Zeitblatt</Link>
          <Link href="/purchases" className={neutralLink}>Bestellen</Link>
          <Link href="/vehicles" className={neutralLink}>Fahrzeuge</Link>
          <Link href="/orders/new" className={accentLink}>Neuer Auftrag</Link>
        </div>
        {isMobile && (
          <button
            type="button"
            className="absolute left-1/2 top-full -translate-x-1/2 border border-t-0 bg-white/95"
            title="Menü ausblenden"
            onClick={() => {
              document.body.dataset.navHidden = "1";
              window.dispatchEvent(new Event("nav-visibility-change"));
            }}
            style={{ width: 54, height: 22, borderBottomLeftRadius: 18, borderBottomRightRadius: 18 }}
          >
            Aus
          </button>
        )}
        <div className={authRowClass}>
          {!session?.user ? (
            <Link href="/login">Login</Link>
          ) : (
            <div className="relative z-[9100]" ref={menuRef}>
              <button
                type="button"
                className="inline-flex items-center gap-3 rounded-full border border-gray-300 bg-white pl-2 pr-3 py-1.5 hover:bg-gray-50"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <span className="inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-gray-100 text-sm font-semibold text-gray-600">
                  {selfEmployee?.profileImagePath ? (
                    <img
                      src={selfEmployee.profileImagePath}
                      alt={displayName ?? "Profil"}
                      className="h-full w-full object-cover"
                      onError={() => setSelfEmployee((current) => current ? { ...current, profileImagePath: null } : current)}
                    />
                  ) : (
                    avatarLabel
                  )}
                </span>
                <span className="text-gray-700">{displayName}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 z-[9200] mt-2 min-w-[220px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="text-sm text-gray-500">Angemeldet als</div>
                    <div className="font-medium text-gray-900">{displayName}</div>
                  </div>
                  <Link
                    href={selfEmployee ? `/employees/${selfEmployee.id}` : "/employees"}
                    className="block px-4 py-3 text-sm text-gray-800 hover:bg-gray-50"
                    onClick={() => setMenuOpen(false)}
                  >
                    Bearbeiten
                  </Link>
                  <button
                    onClick={() => signOut({ callbackUrl: "/login" })}
                    className="block w-full border-t border-gray-100 px-4 py-3 text-left text-sm text-red-700 hover:bg-red-50"
                  >
                    Abmelden
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}
